'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Award, CheckCircle, ArrowRight, Star } from 'lucide-react';
import { ContactModal } from '@/components/contact-modal';
import { Breadcrumb } from '@/components/breadcrumb';

const evaluationTypes = [
  {
    title: '技术水平评价',
    description: '评估科技成果的技术创新性、先进性和成熟度。',
    metrics: ['技术创新程度', '技术成熟度', '技术先进性'],
  },
  {
    title: '创新价值评估',
    description: '评估科技成果的经济价值和社会价值。',
    metrics: ['市场应用前景', '经济效益评估', '社会效益评估'],
  },
  {
    title: '成果认证服务',
    description: '为科技成果提供权威的第三方认证和评价报告。',
    metrics: ['第三方认证', '评价报告', '成果登记'],
  },
];

const evaluationCriteria = [
  { name: '技术创新性', weight: 30, description: '成果的创新程度和技术突破' },
  { name: '技术成熟度', weight: 25, description: '技术的完善程度和可应用性' },
  { name: '应用价值', weight: 25, description: '成果的实际应用价值和前景' },
  { name: '经济社会效益', weight: 20, description: '带来的经济效益和社会效益' },
];

const cases = [
  {
    title: '新能源技术成果评价',
    category: '新能源',
    result: '技术水平达到国际先进',
  },
  {
    title: '智能制造系统评价',
    category: '智能制造',
    result: '创新价值评级AAA',
  },
  {
    title: '生物医药成果认证',
    category: '生物医药',
    result: '科技成果认证通过',
  },
];

export default function EvaluationPage() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '科技成果评价' },
  ];

  return (
    <div className="flex flex-col">
      <Breadcrumb items={breadcrumbItems} />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="h-12 w-12 rounded-lg bg-blue-500/30 flex items-center justify-center mb-6">
              <Award className="h-6 w-6" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight lg:text-5xl">科技成果评价</h1>
            <p className="mt-6 text-lg text-blue-100">
              提供专业的科技成果评价服务，为企业的技术创新成果提供权威认证和价值评估，
              助力企业技术成果转化和产业化应用。
            </p>
            <Button
              size="lg"
              variant="default"
              className="mt-8 bg-white text-blue-900 hover:bg-gray-100"
              onClick={() => setIsContactOpen(true)}
            >
              立即咨询
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Evaluation Types */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">评价服务类型</h2>
            <p className="mt-4 text-lg text-gray-600">全方位的科技成果评价服务</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {evaluationTypes.map((type) => (
              <Card key={type.title} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle>{type.title}</CardTitle>
                  <CardDescription className="text-gray-600">{type.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {type.metrics.map((metric) => (
                      <li key={metric} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-blue-600 mr-2" />
                        {metric}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Evaluation Criteria */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">评价体系</h2>
            <p className="mt-4 text-lg text-gray-600">科学的评价指标体系</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-6">
            {evaluationCriteria.map((criteria) => (
              <div key={criteria.name} className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-blue-600 mr-2" />
                    <span className="font-semibold text-gray-900">{criteria.name}</span>
                  </div>
                  <span className="text-blue-600 font-bold">{criteria.weight}%</span>
                </div>
                <p className="text-sm text-gray-600 ml-7">{criteria.description}</p>
                <div className="mt-3 ml-7 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 rounded-full"
                    style={{ width: `${criteria.weight}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Cases */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">成功案例</h2>
            <p className="mt-4 text-lg text-gray-600">部分科技成果评价案例展示</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {cases.map((caseItem) => (
              <Card key={caseItem.title} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="text-sm text-blue-600 mb-2">{caseItem.category}</div>
                  <CardTitle className="text-lg">{caseItem.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <span className="text-gray-600">{caseItem.result}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold">开始成果评价</h2>
          <p className="mt-4 text-lg text-blue-100 max-w-2xl mx-auto">
            立即联系我们，为您的科技成果获取专业评价
          </p>
          <div className="mt-8">
            <Button
              size="lg"
              variant="default"
              className="bg-white text-blue-600 hover:bg-gray-100"
              onClick={() => setIsContactOpen(true)}
            >
              免费咨询
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </div>
  );
}